import { cmToCanvasUnits } from './floorplanMeasurementUtils'
import type { FloorplanDocument } from '../venueLayout'

export type FloorplanSurfaceMaterialDefinition = {
  id: string
  label: string
  projection: 'floor' | 'wall'
  texturePath: string
  defaultTextureScale: number
  tileSizeCm: number
}

const floorplanSurfaceMaterials: FloorplanSurfaceMaterialDefinition[] = [
  {
    id: 'dark_concrete_floor',
    label: 'Dark Concrete',
    projection: 'floor',
    texturePath: '/floorplan-materials/dark-concrete.png',
    defaultTextureScale: 1,
    tileSizeCm: 100,
  },
  {
    id: 'black_marble_floor',
    label: 'Black Marble',
    projection: 'floor',
    texturePath: '/floorplan-materials/black-marble.png',
    defaultTextureScale: 1,
    tileSizeCm: 120,
  },
  {
    id: 'dark_wood_floor',
    label: 'Dark Wood',
    projection: 'floor',
    texturePath: '/floorplan-materials/dark-wood.png',
    defaultTextureScale: 1,
    tileSizeCm: 120,
  },
  {
    id: 'led_dance_floor',
    label: 'LED Dance Floor',
    projection: 'floor',
    texturePath: '/floorplan-materials/led-dance-floor.png',
    defaultTextureScale: 1,
    tileSizeCm: 100,
  },
  {
    id: 'red_brick_wall',
    label: 'Red Brick',
    projection: 'wall',
    texturePath: '/floorplan-materials/red-brick.png',
    defaultTextureScale: 1,
    tileSizeCm: 150,
  },
  {
    id: 'black_brick_wall',
    label: 'Black Brick',
    projection: 'wall',
    texturePath: '/floorplan-materials/black-brick.png',
    defaultTextureScale: 1,
    tileSizeCm: 150,
  },
  {
    id: 'concrete_wall',
    label: 'Concrete Wall',
    projection: 'wall',
    texturePath: '/floorplan-materials/concrete-wall.png',
    defaultTextureScale: 1,
    tileSizeCm: 100,
  },
  {
    id: 'wood_panel_wall',
    label: 'Wood Panel',
    projection: 'wall',
    texturePath: '/floorplan-materials/wood-panel.png',
    defaultTextureScale: 1,
    tileSizeCm: 120,
  },
  // Legacy aliases to avoid breaking existing floorplans.
  {
    id: 'red_brick',
    label: 'Red Brick (Legacy)',
    projection: 'wall',
    texturePath: '/floorplan-materials/red-brick.png',
    defaultTextureScale: 1,
    tileSizeCm: 150,
  },
  {
    id: 'concrete',
    label: 'Concrete (Legacy)',
    projection: 'floor',
    texturePath: '/floorplan-materials/dark-concrete.png',
    defaultTextureScale: 1,
    tileSizeCm: 100,
  },
  {
    id: 'wood',
    label: 'Wood (Legacy)',
    projection: 'floor',
    texturePath: '/floorplan-materials/dark-wood.png',
    defaultTextureScale: 1,
    tileSizeCm: 120,
  },
]

export function listFloorplanSurfaceMaterials(): FloorplanSurfaceMaterialDefinition[] {
  return floorplanSurfaceMaterials
}

export function listFloorplanSurfaceMaterialsByProjection(
  projection: FloorplanSurfaceMaterialDefinition['projection']
): FloorplanSurfaceMaterialDefinition[] {
  return floorplanSurfaceMaterials.filter(
    (material) => material.projection === projection
  )
}

export function resolveMaterialTextureUrl(
  materialId: string | null | undefined
): string | null {
  const normalized = materialId?.trim().toLowerCase()
  if (!normalized) return null
  return (
    floorplanSurfaceMaterials.find((material) => material.id === normalized)
      ?.texturePath ?? null
  )
}

export function resolveMaterialTextureScale(
  textureScale: number | null | undefined
): number {
  if (!Number.isFinite(textureScale)) return 1
  return Math.max(0.1, Math.min(10, Number(textureScale)))
}

export function getFloorplanSurfaceMaterialById(
  materialId: string | null | undefined
): FloorplanSurfaceMaterialDefinition | null {
  const normalized = materialId?.trim().toLowerCase()
  if (!normalized) return null
  return (
    floorplanSurfaceMaterials.find((material) => material.id === normalized) ??
    null
  )
}

export function resolveFloorPatternScale(args: {
  tileSizeCm: number | null | undefined
  textureScale?: number | null
  imageNaturalWidth: number | null | undefined
  doc: FloorplanDocument
}): { x: number; y: number } {
  const baseTileSizeCm = Number.isFinite(args.tileSizeCm)
    ? Math.max(1, Number(args.tileSizeCm))
    : 100
  const scaleMultiplier = resolveMaterialTextureScale(args.textureScale)
  const naturalWidth = Number.isFinite(args.imageNaturalWidth)
    ? Math.max(1, Number(args.imageNaturalWidth))
    : 1024
  const tileCanvasWidth = cmToCanvasUnits(
    baseTileSizeCm * scaleMultiplier,
    args.doc
  )
  const patternScale = Math.max(0.0001, tileCanvasWidth / naturalWidth)
  return { x: patternScale, y: patternScale }
}

export function parseHex8Color(value: string | null | undefined): {
  r: number
  g: number
  b: number
  a: number
} | null {
  const color = value?.trim()
  if (!color?.startsWith('#')) return null
  const hex = color.slice(1)
  if (hex.length !== 8) return null

  const r = Number.parseInt(hex.slice(0, 2), 16)
  const g = Number.parseInt(hex.slice(2, 4), 16)
  const b = Number.parseInt(hex.slice(4, 6), 16)
  const a = Number.parseInt(hex.slice(6, 8), 16) / 255

  if ([r, g, b, a].some((part) => Number.isNaN(part))) return null

  return { r, g, b, a }
}
